import {
    DebtInvoiceEmailTemplateParams,
    EmailForm,
    EmailTemplateResponse,
    InvoiceAttachmentName,
    InvoiceDebtReminderEmailRequest,
    InvoiceEmailRequest,
} from 'types/emails';
import { InvoiceDebtReminderEmailTemplateType, InvoiceEmailTemplateType } from 'types/invoices.ts';
import { objectToQueryParams } from 'utils/linkUtil.ts';
import { APIInvoiceDetailsSlice } from './invoice-details';
import { APIInvoicesSlice } from './invoices.ts';
import { portalAPI } from './portalAPI';
import { APISentInvoicesSlice } from './sentInvoices.ts';
import { handleRTKQueryError } from './util/errorHandling';

const buildFormData = (emailForm: Omit<EmailForm, 'id' | 'ids'>): FormData => {
    const formData = new FormData();
    emailForm.to.forEach((to) => {
        formData.append('to', to);
    });
    emailForm.cc?.forEach((cc) => {
        formData.append('cc', cc);
    });
    emailForm.bcc?.forEach((cc) => {
        formData.append('bcc', cc);
    });
    emailForm.replyTo?.forEach((replyTo) => {
        formData.append('replyTo', replyTo);
    });
    formData.append('content', emailForm.content);
    formData.append('subject', emailForm.subject);
    emailForm.files?.forEach((file) => {
        formData.append('files', file);
    });
    return formData;
};

//todo imho does not make sense to extract 'email' domain, we should move endpoints to the domain entities (invoice, coi-check etc)
const APIEmailsSlice = portalAPI.injectEndpoints({
    endpoints: (build) => ({
        sendInvoiceEmailMessage: build.mutation<void, Omit<InvoiceEmailRequest, 'id'>>({
            query: ({ ...body }) => {
                const formData = buildFormData(body);

                // Send email directly when "Me" is selected (id === -1), otherwise send Office ID
                if (body.invoicingEmailAddress) {
                    formData.append('invoicingEmailAddress', body.invoicingEmailAddress);
                } else if (body.invoicedByOfficeId && body.invoicedByOfficeId > 0) {
                    formData.append('invoicedByOfficeId', body.invoicedByOfficeId.toString());
                }

                if (body.generatedAttachments) {
                    body.generatedAttachments.forEach((attachment) =>
                        formData.append('generatedAttachments[]', attachment)
                    );
                }
                return {
                    url: `/v1/invoices/send${objectToQueryParams({
                        invoiceIds: body.ids,
                        langCode: body.langCode,
                        transitionToSent: body.transitionToSent,
                    })}`,
                    method: 'POST',
                    body: formData,
                    formData: true,
                };
            },
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            'Invoices',
                            'InvoicesListItem',
                            'InvoicesCount',
                            'InvoicesTotals',
                        ])
                    );
                    dispatch(APISentInvoicesSlice.util.invalidateTags(['SentInvoices']));
                    dispatch(APIInvoiceDetailsSlice.util.invalidateTags(['InvoiceMatters']));
                } catch (error) {
                    handleRTKQueryError(error, 'sendInvoiceEmailMessage', arg);
                }
            },
        }),
        markAsSent: build.mutation<void, number>({
            query: (id) => {
                return {
                    url: `/v1/invoices/${id}/mark-as-sent`,
                    method: 'POST',
                };
            },
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        APIInvoicesSlice.util.invalidateTags([
                            'Invoices',
                            'InvoicesListItem',
                            'InvoicesCount',
                            'InvoicesTotals',
                        ])
                    );
                    dispatch(APISentInvoicesSlice.util.invalidateTags(['SentInvoices']));
                    dispatch(APIInvoiceDetailsSlice.util.invalidateTags(['InvoiceMatters']));
                } catch (error) {
                    handleRTKQueryError(error, 'markAsSent', arg);
                }
            },
        }),
        sendCoiCheckEmailMessage: build.mutation<void, Omit<EmailForm, 'id'>>({
            query: ({ ...body }) => {
                const formData = buildFormData(body);
                body.ids.forEach((id) => formData.append('ids[]', id));
                return {
                    url: `/v1/coi-checks/emails/send`,
                    method: 'POST',
                    body: formData,
                    formData: true,
                };
            },
        }),
        sendDebtInvoiceReminderMessage: build.mutation<void, InvoiceDebtReminderEmailRequest>({
            query: ({ ...body }) => {
                const formData = buildFormData(body);

                formData.append('includePdfs', body.includePdfs?.toString() || 'false');
                formData.append('includePenaltyPdfs', body.includePenaltyPdfs?.toString() || 'false');
                formData.append('debtReminderType', body.debtReminderType);
                formData.append('debtReminderScopeType', body.debtReminderScopeType);

                if (body.invoicingEmailAddress) {
                    formData.append('invoicingEmailAddress', body.invoicingEmailAddress);
                } else if (body.invoicedByOfficeId && body.invoicedByOfficeId > 0) {
                    formData.append('invoicedByOfficeId', body.invoicedByOfficeId.toString());
                }

                if (body.additionalDebtReminderText) {
                    formData.append('additionalDebtReminderText', body.additionalDebtReminderText);
                }
                return {
                    url: `/v1/invoices/send-debt-reminder${objectToQueryParams({
                        invoiceIds: body.invoiceIds.join(','),
                        langCode: body.langCode,
                    })}`,
                    method: 'POST',
                    body: formData,
                    formData: true,
                };
            },
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                await queryFulfilled;
                dispatch(APIInvoicesSlice.util.invalidateTags(['InvoicesListItem']));
            },
        }),
        getInvoicesEmailTemplate: build.query<
            EmailTemplateResponse,
            {
                invoiceIds: number[];
                templateType: InvoiceEmailTemplateType;
                langCode?: string;
            }
        >({
            query: (params) => {
                return `/v1/email-templates/invoices/bulk/${params.invoiceIds}${objectToQueryParams({
                    langCode: params.langCode,
                    templateType: params.templateType,
                })}`;
            },
            keepUnusedDataFor: 0,
        }),
        getDebtInvoiceEmailTemplate: build.query<EmailTemplateResponse, DebtInvoiceEmailTemplateParams>({
            query: (params) =>
                `/v1/email-templates/debt-reminders${objectToQueryParams({
                    langCode: params.langCode,
                    invoiceIds: params.invoiceIds,
                    type: params.debtReminderType,
                    scope: params.debtReminderScopeType,
                })}`,
            keepUnusedDataFor: 0,
        }),
        getAvailableLanguages: build.query<string[], InvoiceEmailTemplateType | InvoiceDebtReminderEmailTemplateType>({
            query: (templateType) => `/v1/email-templates/available-languages/${templateType}`,
            keepUnusedDataFor: 60 * 10,
        }),
        getInvoiceAttachmentNames: build.query<InvoiceAttachmentName[], number[]>({
            query: (invoiceIds) => `/v1/invoices/email-attachment-names?invoiceIds=${invoiceIds.join(',')}`,
            keepUnusedDataFor: 0,
        }),
    }),
    overrideExisting: false,
});

export const {
    useSendInvoiceEmailMessageMutation,
    useMarkAsSentMutation,
    useSendCoiCheckEmailMessageMutation,
    useGetAvailableLanguagesQuery,
    useGetDebtInvoiceEmailTemplateQuery,
    useSendDebtInvoiceReminderMessageMutation,
    useGetInvoicesEmailTemplateQuery,
    useGetInvoiceAttachmentNamesQuery,
} = APIEmailsSlice;
